import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  Chip,
  Divider,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  HelpOutline,
  ArrowForward,
  Edit as DraftIcon,
  Send as SubmittedIcon,
  ThumbUp as ApprovedIcon,
  AttachMoney as PaidIcon,
  Cancel as DeniedIcon,
} from '@mui/icons-material';

/**
 * WorkflowGuide - Shows the timesheet approval workflow with visual steps
 *
 * Usage:
 *   <WorkflowGuide />
 */
export default function WorkflowGuide({ showLabel = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const workflowSteps = [
    {
      status: 'pending',
      label: 'Draft',
      color: 'default',
      icon: <DraftIcon fontSize="small" />,
      description: 'Entry created but not submitted for approval',
      action: 'Employee creates time entry',
    },
    {
      status: 'submitted',
      label: 'Submitted',
      color: 'info',
      icon: <SubmittedIcon fontSize="small" />,
      description: 'Waiting for manager/admin review',
      action: 'Employee submits for approval',
    },
    {
      status: 'approved',
      label: 'Approved',
      color: 'success',
      icon: <ApprovedIcon fontSize="small" />,
      description: 'Manager approved, ready for payroll',
      action: 'Manager approves entry',
    },
    {
      status: 'paid',
      label: 'Paid',
      color: 'primary',
      icon: <PaidIcon fontSize="small" />,
      description: 'Payment processed, entry complete',
      action: 'Admin marks as paid',
    },
  ];

  const deniedStep = {
    status: 'denied',
    label: 'Denied',
    color: 'error',
    icon: <DeniedIcon fontSize="small" />,
    description: 'Entry rejected, needs correction',
    action: 'Manager denies with reason',
  };

  return (
    <>
      <Tooltip title="View workflow guide">
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label="Workflow guide"
          sx={{ ml: 0.5 }}
        >
          <HelpOutline fontSize="small" color="action" />
        </IconButton>
      </Tooltip>
      {showLabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ cursor: 'pointer', ml: 0.5 }}
          onClick={handleClick}
        >
          How it works
        </Typography>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, maxWidth: isMobile ? '90vw' : 400 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Timesheet Approval Workflow
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Time entries follow this approval process:
          </Typography>

          {/* Main workflow path */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {workflowSteps.map((step, index) => (
              <Box key={step.status}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={step.icon}
                    label={step.label}
                    color={step.color}
                    size="small"
                    sx={{ minWidth: 100 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                {index < workflowSteps.length - 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 5, my: 0.5 }}>
                    <ArrowForward fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {workflowSteps[index + 1].action}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Denied path */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            If an entry needs correction:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={deniedStep.icon}
              label={deniedStep.label}
              color={deniedStep.color}
              size="small"
              sx={{ minWidth: 100 }}
            />
            <Typography variant="caption" color="text.secondary">
              {deniedStep.description}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 1 }}>
            Denied entries can be edited and resubmitted.
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Quick tips */}
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Quick Tips:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="caption" color="text.secondary">
              Submit entries before the pay period ends
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              Add notes to explain unusual hours or entries
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              Contact your manager for urgent approvals
            </Typography>
          </Box>
        </Paper>
      </Popover>
    </>
  );
}

/**
 * StatusChipWithHelp - Status chip with inline tooltip explanation
 */
export function StatusChipWithHelp({ status, size = 'small' }) {
  const statusConfig = {
    pending: { label: 'Draft', color: 'default', tip: 'Not yet submitted for approval' },
    submitted: { label: 'Submitted', color: 'info', tip: 'Waiting for manager review' },
    approved: { label: 'Approved', color: 'success', tip: 'Ready for payroll processing' },
    paid: { label: 'Paid', color: 'primary', tip: 'Payment has been processed' },
    denied: { label: 'Denied', color: 'error', tip: 'Rejected - check manager notes' },
  };

  const config = statusConfig[status] || { label: status, color: 'default', tip: '' };

  return (
    <Tooltip title={config.tip} arrow>
      <Chip label={config.label} color={config.color} size={size} />
    </Tooltip>
  );
}
